import { AdminDeleteArtifactsData, adminDeleteArtifacts, chatCreateArtifact, resetChat } from "@/api"
import { appState } from "@/app/appState"
import { ChatObjectType, chatState } from "@/app/chatState"
import { artifact, artifact_list_item } from "@/app/typing/artifacts"
import { ChatMessage, ChatMessageBlockUserAction } from "@/app/typing/chat"
import { Request } from "@/components/CRUD"
import { NavBarGroup } from "@/components/navbar/workspaceNavbar"
import { ScrollBox } from "@/components/UI/page"
import { Add_new, AddObject, Remove } from "@/components/UI/svgs"
import { ActionHandler, ChatListItem, useRefState } from "@/components/UI/tools"
import { keys, set } from "mobx"
import { observer } from "mobx-react-lite"
import { useEffect, useMemo, useRef, useState } from "react"

// ---------------------------------------------------------------------------
// Citation highlight (Option A): after an artifact citation is clicked we open
// the artifact and paint a transient flash over the cited span in the rendered
// rich-text view. Matching is by CONTENT (whitespace-flexible, "[...]"-aware),
// so the frontend needs no wrap/line-number logic. We never mutate the
// Lexical-controlled contentEditable DOM — the CSS Custom Highlight API paints
// the range out-of-band (see ::highlight(citation-flash) in globals.css).
// ---------------------------------------------------------------------------

const CITATION_HIGHLIGHT_NAME = "citation-flash"

// Turn a cited_text payload into a whitespace-flexible regex. "[...]" is a
// preservation wildcard the model uses to skip unchanged middle text, so each
// segment is matched independently with "any text" allowed between segments.
function buildCitationRegex(citedText: string): RegExp | null {
    // Unescape the escapes LLMs emit inside ${...,"cited_text"} tokens.
    const unescaped = citedText
        .replace(/\\"/g, '"')
        .replace(/\\n/g, " ")
        .replace(/\\t/g, " ")

    const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const segments = unescaped
        .split("[...]")
        .map((s) => s.trim())
        .filter(Boolean)
        // Collapse each segment's internal whitespace to \s+ so soft-wrap spaces
        // and rendered layout differences still match the raw cited text.
        .map((seg) => escapeRe(seg).replace(/\s+/g, "\\s+"))

    if (segments.length === 0) return null
    try {
        return new RegExp(segments.join("[\\s\\S]*?"), "i")
    } catch {
        return null
    }
}

// Flatten a subtree's text into one string plus a per-character map back to the
// owning text node + offset, so a regex match can be rebuilt into a DOM Range.
function collectTextIndex(root: Element): { text: string; chars: { node: Text; offset: number }[] } {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
    let text = ""
    const chars: { node: Text; offset: number }[] = []
    let n: Node | null
    while ((n = walker.nextNode())) {
        const t = n as Text
        for (let i = 0; i < t.data.length; i++) chars.push({ node: t, offset: i })
        text += t.data
    }
    return { text, chars }
}

// Locate cited_text inside `scope` and paint a transient flash over it.
// Returns false when the scope/text isn't present yet so the caller can retry.
function flashCitationInScope(scopeSelector: string, citedText: string): boolean {
    if (!citedText || typeof citedText !== "string") return false
    const scope = document.querySelector(scopeSelector)
    if (!scope) return false

    const re = buildCitationRegex(citedText)
    if (!re) return false

    const { text, chars } = collectTextIndex(scope)
    if (chars.length === 0) return false
    const match = re.exec(text)
    if (!match) return false

    const start = match.index
    const end = Math.min(match.index + match[0].length, chars.length)
    const startChar = chars[start]
    const endChar = chars[end - 1]
    if (!startChar || !endChar) return false

    const range = document.createRange()
    range.setStart(startChar.node, startChar.offset)
    range.setEnd(endChar.node, endChar.offset + 1)

    // Scroll the start of the cited span into view.
    startChar.node.parentElement?.scrollIntoView({ behavior: "smooth", block: "center" })

    // Paint out-of-band; degrade to scroll-only where the Highlight API is absent.
    const cssHighlights = (CSS as any)?.highlights
    const HighlightCtor = (window as any).Highlight
    if (cssHighlights && HighlightCtor) {
        cssHighlights.set(CITATION_HIGHLIGHT_NAME, new HighlightCtor(range))
        setTimeout(() => cssHighlights.delete(CITATION_HIGHLIGHT_NAME), 2500)
    }
    return true
}

// The object content mounts asynchronously (lazy MDXEditor or PDF render + fetch),
// so poll briefly until the scope and cited text appear, then give up quietly.
function scrollAndFlashCitation(objectType: "artifact" | "document", objectId: number, citedText: string) {
    const selector = `[data-object-content="${objectType}-${objectId}"]`
    const started = Date.now()
    const tick = () => {
        if (flashCitationInScope(selector, citedText)) return
        if (Date.now() - started > 4000) return
        setTimeout(tick, 120)
    }
    tick()
}

const ChatObjectsView = observer(({ tagIds }: { tagIds: number[] }) => {

    const [search, setSearch] = useState<string>("")
    const searchRegex = useMemo(() => new RegExp(search.split('').map(x => `[${x.toLowerCase()}${x.toUpperCase()}]`).join('.*')), [search])

    useEffect(() => {

        const handleCitationClick = (e: CustomEvent) => {
            if (e.detail.citationType == "artifact") {
                console.log("Citation click received in ChatObjectsView", e.detail);
                const { artifact_id, cited_text } = e.detail;

                chatState.getObjectByKey({ type: "artifact", id: artifact_id }) // Ensure the object is loaded
                chatState.chatObjects.update([...chatState.chatObjects.entries()].filter(x => (x.value.type == "artifact" && x.value.id == artifact_id)).map(x => ({ key: x.key, value: { ...x.value, isSelected: true } })))
                chatState.setSelectedObjectKey({ type: "artifact", id: artifact_id }, false);

                // Scroll to + flash the cited span in the rendered artifact view so the
                // click has the same "jump to source" behaviour event citations have.
                if (cited_text) scrollAndFlashCitation("artifact", artifact_id, cited_text);

                return
            } else if (e.detail.citationType == "document") {
                console.log("Document citation click received in ChatObjectsView", e.detail);
                const { document_id, cited_text } = e.detail;

                chatState.getObjectByKey({ type: "document", id: document_id }) // Ensure the object is loaded
                chatState.chatObjects.update([...chatState.chatObjects.entries()].filter(x => (x.value.type == "document" && x.value.id == document_id)).map(x => ({ key: x.key, value: { ...x.value, isSelected: true } })))
                chatState.setSelectedObjectKey({ type: "document", id: document_id }, false);

                // Documents render as their own extracted text in the viewer, so the
                // same text-search flash works. It can miss when the viewer's PDF text
                // extraction differs from the backend's, which degrades to scroll-only.
                if (cited_text) scrollAndFlashCitation("document", document_id, cited_text);

                return
            } else if (e.detail.citationType == "event") {
                console.log("Event citation click received in ChatObjectsView", e.detail);
                const { event_id, start_line, end_line } = e.detail;

                chatState.getObjectByKey({ type: "event", id: event_id }) // Ensure the object is loaded
                chatState.chatObjects.update([...chatState.chatObjects.entries()].filter(x => (x.value.type == "event" && x.value.id == event_id)).map(x => ({ key: x.key, value: { ...x.value, isSelected: true } })))
                chatState.setSelectedObjectKey({ type: "event", id: event_id }, false);

                // Jump to the cited line in the event OEW transcription pane. `start_line`/`end_line`
                // are in the backend wrapped numbering; the component maps them to the matching
                // segments once the transcription loads and highlights the whole cited range.
                chatState.setPendingTranscriptionScroll({
                    eventId: event_id,
                    startLine: start_line,
                    endLine: end_line,
                    nonce: Date.now(),
                });

                return
            }

            // LEGACY support for tool citations that reference artifacts/events by document_title
            if (e.detail.citationType == "tool") {
                const { document_title } = e.detail;

                if (document_title.type === 'artifact') {
                    // Scroll to the item or perform other actions
                    chatState.chatObjects.update([...chatState.chatObjects.entries()].filter(x => (x.value.type == "artifact" && x.value.id == document_title.id)).map(x => ({ key: x.key, value: { ...x.value, isSelected: true } })))
                }


                return
            }

        };

        document.addEventListener('citation-click', handleCitationClick as EventListener);

        return () => {
            document.removeEventListener('citation-click', handleCitationClick as EventListener);
        };
    }, []);





    const [reload, setReload] = useState(0);

    useEffect(() => {
        setReload(r => r + 1);
    }, [chatState.chatObjects.size])


    const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };

    return (
        <div key={reload} className="overflow-x-hidden h-full py-1 w-full flex flex-col gap-1">

            <ScrollBox hideScrollbar reverse>
                <div className="timeline-rail sticky bottom-1">
                    <input
                        className="sidebar-filter"
                        type="text"
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={appState.t("common.filterPlaceholder")}
                    />
                </div>
                <div className="flex flex-col items-start w-fit select-none mt-4 mb-3 gap-1s">
                    {(() => {
                        const filterTree = (nodes: any[]): any[] => {
                            return nodes.map(node => {
                                const filteredChildren = node.children ? filterTree(node.children) : [];
                                return { ...node, children: filteredChildren };
                            }).filter(node => {
                                const matches = node.value && (
                                    ("artifact" in node.value && searchRegex?.test(node.value.artifact.variable_name)) ||
                                    ("event" in node.value && searchRegex?.test(node.value.event.title)) ||
                                    ("document" in node.value && searchRegex?.test(node.value.document.variable_name))
                                )
                                return matches || (node.children && node.children.length > 0);
                            });
                        };

                        const filteredRoots = filterTree(chatState.chatObjects.entries());

                        const timestamps = filteredRoots.map(x => new Date(x.value.created_at).getTime());
                        const gaps: { index: number, diff: number }[] = [];
                        for (let i = 0; i < timestamps.length - 1; i++) {
                            gaps.push({ index: i, diff: Math.abs(timestamps[i + 1] - timestamps[i]) });
                        }

                        const N = 5;
                        const topNGaps = [...gaps].sort((a, b) => b.diff - a.diff).slice(0, N);
                        const maxGap = topNGaps[0]?.diff ?? 1;
                        const gapMap = new Map(topNGaps.map(g => [g.index, { height: Math.max(20, (g.diff / maxGap) * 100), diff: g.diff }]));

                        const formatDuration = (ms: number) => {
                            const seconds = Math.floor(ms / 1000);
                            const minutes = Math.floor(seconds / 60);
                            const hours = Math.floor(minutes / 60);
                            const days = Math.floor(hours / 24);

                            if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
                            if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
                            if (minutes > 0) return `${minutes} min${minutes > 1 ? 's' : ''}`;
                            return '< 1 min';
                        }

                        const renderNode = (x: any, rootKey: any, depth = 0, parentKey: any = null, indexInParent: number = -1): React.ReactNode => {
                            const hasChildren = x.children && x.children.length > 0;
                            // isSubSelected: this node is the currently open object in the editor
                            const isSubSelected = parentKey
                                ? chatState.chatObjects.get(parentKey)?.child_index === indexInParent
                                && chatState.selectedObjectKey?.type === parentKey.type
                                && chatState.selectedObjectKey?.id === parentKey.id
                                : false;

                            const handleClick = (e: React.MouseEvent) => {
                                if (e.shiftKey) {
                                    // Build a flat ordered list: event root is the first entry in its group,
                                    // followed by its children in insertion order.
                                    type FlatItem = { key: { type: ChatObjectType, id: number }, parentKey: { type: ChatObjectType, id: number } | null, indexInParent: number };
                                    const flatList: FlatItem[] = [];
                                    for (const root of filteredRoots) {
                                        if (root.value.msgId)
                                            flatList.push({ key: root.key, parentKey: null, indexInParent: -1 });
                                        if (root.children && root.children.length > 0) {
                                            root.children.forEach((child: any, ci: number) => {
                                                if (chatState.chatObjects.get(child.key)?.msgId)
                                                    flatList.push({ key: child.key, parentKey: root.key, indexInParent: ci });
                                            });
                                        }
                                    }

                                    // Find anchor: current selectedObjectKey + optional child_index
                                    const sk = chatState.selectedObjectKey;
                                    let anchorIdx = -1;
                                    if (sk) {
                                        const skChildIdx = chatState.chatObjects.get(sk)?.child_index;
                                        if (skChildIdx !== undefined) {
                                            anchorIdx = flatList.findIndex(item =>
                                                item.parentKey?.type === sk.type && item.parentKey?.id === sk.id && item.indexInParent === skChildIdx
                                            );
                                        } else {
                                            anchorIdx = flatList.findIndex(item =>
                                                item.key.type === sk.type && item.key.id === sk.id && item.parentKey === null
                                            );
                                        }
                                    }

                                    // Find target — for any node just look up its own flat position
                                    const targetIdx = parentKey
                                        ? flatList.findIndex(item => item.key.type === x.key.type && item.key.id === x.key.id)
                                        : flatList.findIndex(item => item.key.type === x.key.type && item.key.id === x.key.id && item.parentKey === null);

                                    if (anchorIdx !== -1 && targetIdx !== -1) {
                                        const [start, end] = anchorIdx <= targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
                                        chatState.chatObjects.update(
                                            flatList.slice(start, end + 1).map(item => ({ key: item.key, value: { isSelected: true } }))
                                        );
                                    }

                                    // Move OEW focus to the clicked target while keeping selections
                                    const focusTarget = targetIdx !== -1 ? flatList[targetIdx] : null;
                                    if (focusTarget?.parentKey) {
                                        chatState.setSelectedObjectKey(focusTarget.parentKey, true, false, false, focusTarget.key, false);
                                        chatState.chatObjects.update([{ key: focusTarget.parentKey, value: { child_index: focusTarget.indexInParent } }]);
                                    } else if (focusTarget) {
                                        chatState.setSelectedObjectKey(focusTarget.key, true, false, false, undefined, false);
                                        chatState.chatObjects.update([{ key: focusTarget.key, value: { isSelected: true, child_index: undefined } }]);
                                    } else if (parentKey) {
                                        chatState.setSelectedObjectKey(rootKey, true, false, false, x.key, false);
                                        chatState.chatObjects.update([{ key: x.key, value: { isSelected: true } }, { key: parentKey, value: { child_index: indexInParent } }]);
                                    } else {
                                        chatState.setSelectedObjectKey(rootKey, true);
                                    }
                                    return;
                                }

                                if (parentKey) {
                                    // Child node: selectedObjectKey stays on the parent event;
                                    // isSelected lives on the child object itself.
                                    if (e.ctrlKey) {
                                        const currentlySelected = chatState.chatObjects.get(x.key)?.isSelected ?? false;
                                        if (currentlySelected) {
                                            // Deselect: only redirect OEW if this child is the one currently open
                                            chatState.chatObjects.update([{ key: x.key, value: { isSelected: false } }]);
                                            if (isSubSelected) {
                                                const siblings = chatState.chatObjects.entries([parentKey])[0]?.children ?? [];
                                                const nextIdx = siblings.findIndex((child: any) =>
                                                    !(child.key.type === x.key.type && child.key.id === x.key.id) &&
                                                    (chatState.chatObjects.get(child.key)?.isSelected ?? false)
                                                );
                                                if (nextIdx !== -1) {
                                                    chatState.setSelectedObjectKey(parentKey, true, false, false, siblings[nextIdx].key, false);
                                                    chatState.chatObjects.update([{ key: parentKey, value: { child_index: nextIdx } }]);
                                                } else {
                                                    chatState.chatObjects.update([{ key: parentKey, value: { child_index: undefined } }]);
                                                }
                                            }
                                        } else {
                                            // Select: add to selection and open in OEW
                                            chatState.setSelectedObjectKey(rootKey, true, false, false, x.key, false);
                                            chatState.chatObjects.update([
                                                { key: x.key, value: { isSelected: true } },
                                                { key: parentKey, value: { child_index: indexInParent } },
                                            ]);
                                        }
                                    } else {
                                        // Plain click: same open behaviour as clicking the object in the chat.
                                        chatState.openObject(x.key);
                                    }
                                } else {
                                    // Root node (with or without children): open it and mark as selected
                                    if (e.ctrlKey) {
                                        const currentlySelected = chatState.chatObjects.get(x.key)?.isSelected ?? false;
                                        if (currentlySelected) {
                                            // Deselect: only redirect OEW if this root is currently open in the OEW
                                            const isOpenInOEW = chatState.selectedObjectKey?.type === x.key.type && chatState.selectedObjectKey?.id === x.key.id;
                                            chatState.chatObjects.update([{ key: rootKey, value: { isSelected: false, child_index: undefined } }]);
                                            if (isOpenInOEW) {
                                                const firstChild = hasChildren ? (x.children.filter((x: any) => x.value.isSelected)?.[0] ?? undefined) : undefined;
                                                if (firstChild) {

                                                    chatState.setSelectedObjectKey(rootKey, true, false, false, firstChild.key, false);
                                                    chatState.chatObjects.update([{ key: rootKey, value: { child_index: x.children.findIndex((child: any) => child.key === firstChild.key) } }]);
                                                } else {
                                                    const nextSelected = [...chatState.chatObjects].find(([_, val]) =>
                                                        val.isSelected && !(val.type === x.key.type && val.id === x.key.id)
                                                    );
                                                    if (nextSelected) {
                                                        const [_, val] = nextSelected;
                                                        const nextKey = { type: val.type, id: val.id };
                                                        const nextParent = val.parent;
                                                        if (nextParent) {
                                                            const siblings = chatState.chatObjects.entries([nextParent])[0]?.children ?? [];
                                                            const nextIdx = siblings.findIndex((s: any) => s.key.type === nextKey.type && s.key.id === nextKey.id);
                                                            chatState.setSelectedObjectKey(nextParent, true, false, false, nextKey, false);
                                                            chatState.chatObjects.update([{ key: nextParent, value: { child_index: nextIdx !== -1 ? nextIdx : undefined } }]);
                                                        } else {
                                                            chatState.setSelectedObjectKey(nextKey, true, false, false, undefined, false);
                                                        }
                                                    } else {
                                                        chatState.setSelectedObjectKey(null);
                                                    }
                                                }

                                            }
                                        } else {
                                            chatState.setSelectedObjectKey(rootKey, true, false, false, undefined, false);
                                            chatState.chatObjects.update([{ key: rootKey, value: { isSelected: true, child_index: undefined } }]);
                                        }
                                    } else {
                                        chatState.openObject(rootKey);
                                    }
                                    if (x.value.revisions?.length ?? 0)
                                        chatState.chatObjects.update([{ key: x.key, value: { revision_index: x?.value.revisions?.filter((r: any) => r != undefined)?.length ? x?.value.revisions?.filter((r: any) => r != undefined)?.length - 1 : undefined } }])
                                }
                            };

                            return hasChildren ? (
                                <NavBarGroup key={`${x.key.type}-${x.key.id}`} isVertical={true}>
                                    <div>
                                        <ChatListItem
                                            onClick={handleClick}
                                            hasRevisions={(x.value.revisions?.length ?? 0) > 0}
                                            hasChanges={x.value.unsaved_changes ?? false}
                                            isSelected={x.value.isSelected ?? false}
                                            isDisabled={false}
                                            isTransparent={true}
                                        >
                                            {`${x.value.label}`}
                                        </ChatListItem>
                                    </div>
                                    {hasChildren && (
                                        <div className="flex flex-col gap-1s">
                                            {x.children.map((child: any, index: number) => renderNode(child, rootKey, depth + 1, x.key, index))}
                                        </div>
                                    )}
                                </NavBarGroup>
                            ) : (
                                <div key={`${x.key.type}-${x.key.id}`} className={`h-fit`}>
                                    <ChatListItem
                                        onClick={handleClick}
                                        hasRevisions={(x.value.revisions?.length ?? 0) > 0}
                                        hasChanges={x.value.unsaved_changes ?? false}
                                        isSelected={x.value.isSelected ?? false}
                                        isDisabled={false}
                                        isTransparent={depth > 0 ? true : false}
                                    >
                                        {`${x.value.label}`}
                                    </ChatListItem>
                                </div>
                            );
                        };

                        const listItems = filteredRoots.flatMap((x, i) => {
                            const item = renderNode(x, x.key, 0);

                            if (gapMap.has(i)) {
                                const gap = gapMap.get(i)!;
                                return [item, (
                                    <div
                                        key={`gap-${i}`}
                                        style={{ height: `${gap.height}px` }}
                                        className="timeline-rail"
                                    >
                                        <span className="sidebar-caption">
                                            {formatDuration(gap.diff)}
                                        </span>
                                    </div>
                                )];
                            }
                            return [item];
                        });

                        if (filteredRoots.length > 0) {
                            const formatDate = (dateStr: string) => {
                                try {
                                    return new Date(dateStr).toLocaleDateString(undefined, dateOptions);
                                } catch {
                                    return "";
                                }
                            };

                            const firstDate = formatDate(filteredRoots[0].value.created_at);
                            const lastDate = formatDate(filteredRoots[filteredRoots.length - 1].value.created_at);

                            const createLabel = (key: string, text: string) => (
                                <div
                                    key={key}
                                    style={{ height: '15px' }}
                                    className="timeline-rail"
                                >
                                    <span className="sidebar-caption">
                                        {text}
                                    </span>
                                </div>
                            );

                            return [
                                createLabel('start-label', firstDate),
                                ...listItems,
                                createLabel('end-label', lastDate)
                            ];
                        }

                        return listItems;
                    })()}
                </div>
                <div className="h-1 w-full flex-shrink-0" />
            </ScrollBox >
            <div className={`bottom-1 flex flex-row pl-1 b`}>

                <ActionHandler
                    title={appState.t("chat.reset")}
                    funq={async () => {
                        appState.setModal({
                            title: appState.t("common.confirmTitle"),
                            body: appState.t("chat.resetConfirm"),
                            action: async () => {
                                const res = await resetChat({
                                    body: {
                                        tag_ids: tagIds
                                    }
                                })
                                if (res) {
                                    const conversationId = chatState.conversationId;
                                    chatState.clear();
                                    chatState.forgetConversationSelection(conversationId);
                                }
                            },
                        });
                    }}
                    disabled={false}
                >
                    <div className="reset-swatch"></div>
                </ActionHandler>

            </div>
        </div >
    )
}
)
export default ChatObjectsView